<template>
    <div style="overflow: auto; color: #050F2D;">
        <div class="ma-4" style="margin-bottom: 24px !important;">
            <div style="font-size: 16px; margin-bottom: 12px; margin-left: 1px;" class="hyperlegible-medium">Intervention Schedule</div>
            <div style="border: 1px solid #e0e1e6; padding: 24px; border-radius: 4px;">
                <div class="d-flex" style="justify-content: space-between">
                    <div class="d-flex align-center">
                        <v-icon class="mr-2" color="#050F2D">far fa-calendar-alt</v-icon>
                        <span v-if="schedule.frequency_cd !== 'SPECIFIC'">{{ $dayjs(schedule.start_date).format('M/D/YYYY') }} - {{ $dayjs(schedule.end_date).format('M/D/YYYY') }}</span>
                        <span v-else class="d-flex" style="flex-wrap: wrap; max-width: 65%">
                            <template v-if="schedule.specific_dates.length">
                                <span v-for="(date, i) in schedule.specific_dates" :key="`date-${i}`">
                                    {{ $dayjs(date).format('M/D/YYYY') }}<span v-if="i < schedule.specific_dates.length - 1" class="mr-1">,</span>
                                </span>
                            </template>
                        </span>
                        <v-divider class="mr-4 ml-4" vertical />
                        <v-icon class="mr-2" color="#050F2D">far fa-clock</v-icon>
                        <div v-if="schedule.start_time && schedule.end_time">
                            {{ $dayjs('2020-01-01 ' + schedule.default_start_time).format("h:mm A") }} - {{ $dayjs('2020-01-01 ' + schedule.default_end_time).format("h:mm A") }}
                        </div>
                        <div v-else style="min-width: 80px">
                            No times set
                        </div>
                        <v-divider v-if="schedule.frequency_cd !== 'SPECIFIC'" class="mr-4 ml-4" vertical />
                        <v-icon v-if="schedule.frequency_cd !== 'SPECIFIC'" class="mr-2" color="#050F2D">far fa-calendar-day</v-icon>
                        {{ frequencyDisplay }}
                    </div>
                    <div v-if="schedule.id" @click="doEditIntvSched" 
                        style="color: #006C90; cursor: pointer; min-width: 60px; width: 60px; margin: auto 0"
                        aria-label="Edit intervention schedule"
                        tabindex="0"
                        @keydown.enter="doEditIntvSched"
                        @keydown.space.prevent="doEditIntvSched"
                        role="button"
                    >
                        <v-icon class="mr-2" color="#006C90">far fa-pencil</v-icon>Edit
                    </div>
                </div>
            </div>
        </div>

        <div v-if="plan.category_id && plan.category_id !== 4 && plan.category_id !== 0" class="ma-4">
            <div style="font-size: 16px; margin-bottom: 4px; margin-left: 1px;" class="hyperlegible-medium">Intervention Review Reminder Emails</div>
            <div style="color: #495B8F; margin-bottom: 4px; margin-left: 1px;">Emails include information about student progress, attendance, and participation in this intervention plan.</div>
            <banner 
                v-if="showBanner"
                class="mt-5 mb-5"
                usage="info" 
                closable 
                title="Updates to Review Schedule" 
                text="You can now turn off the email reminders for the intervention review. If the email is enabled, it will be delivered throughout the duration of the intervention at the frequency specified." 
                @close="closeBanner"
            />
            <div class="d-flex" style="height: 300px;">
                <fe-grid
                    ref="reminderGrid"
                    style="height: 100%; width: 100%;"
                    :columnDefs="schedColDefs"
                    :showAddRowBtn="false"
                    :showToolbar="false"
                    :bordered="false"
                    :gridOptionOverrides="gridOptions"
                    :rowData="remindersData"
                />
            </div>
        </div>

        <sched-edit
            v-if="editSchedRec"
            :model="editSchedRec"
            @update="updateSchedule"
        />

        <fe-dialog
            v-if="editIntvSched"
            title="Edit Schedule"
            acceptButtonText="Update"
            dismissButtonText="Cancel"
            disableAutoclose
            @dismiss="editIntvSched = false"
            @close="editIntvSched = false"
            @accept="$refs.schedule.clearUnusedFields(); confirmIntvSchedEdit = true"
            :acceptButtonDisabled="!intvSchedIsValid"
            :width="'575'"
        >
            <intervention-fullscreen-schedule v-model="editingSchedule" :isAttendance="plan.category_id === 0" :timeRequired="plan.category_id !== 4 && plan.category_id !== 0" standalone ref="schedule" @valid="intvSchedIsValid = $event"/>
        </fe-dialog>

        <fe-dialog
            v-if="confirmIntvSchedEdit"
            title="Override Plan?"
            acceptButtonText="Confirm"
            dismissButtonText="Cancel"
            @accept="updateIntvSched"
            @dismiss="confirmIntvSchedEdit = false; editIntvSched = false;"
        >
            <div class="mb-3">
                <div v-for="change in changesToBeApplied" :key="change.key">
                    {{ change.key }}: {{ change.originalValue }} <v-icon v-if="change.originalValue" class="far fa-arrow-right mx-1" style="font-size: 12px; vertical-align: 0;"></v-icon> {{ change.editedValue }}
                </div>
            </div>
            <div>Saving this record will update the entire intervention plan for <b>all active</b> students.</div>
            If you are trying to create a new phase go to
            <a href="#" @click="doNewPhase">create a new phase</a>. 
            Creating a new phase will end the current intervention plan and create a new intervention group.
        </fe-dialog>

        <fe-dialog
            v-if="editEmailFrequency"
            @accept="updateScheduleOnChange(reminder); editEmailFrequency=false"
            @dismiss="editEmailFrequency=false"
            width="500"
            height="400"
            acceptButtonText="Update"
            dismissButtonText="Cancel"
            title="Edit Reminder Emails"
        >
            <div class="text-muted mt-1 mb-2" style="color:#495B8F">Emails include information about student progress, attendance, and participation in this intervention plan.</div>
            <div class="text-muted mb-4">Send reminder emails:</div>
            <v-radio-group :dense="true" v-model="reminder.frequency" class="mt-2 intervention-creation-reminder-radio-group">
                <v-radio class="intervention-creation-reminder-radio" value="MONTH" style="height:35px;" data-test="intervention-edit-schedule-reminder-frequency-monthly-radio">
                    <template v-slot:label>
                        <span style="font-size: 14px;">Monthly</span> 
                        <div v-if="reminder.frequency === 'MONTH'" class="d-flex">
                            <div class="ml-1 align-self-center" style="font-size: 14px;"> on the </div>
                            <fe-remote-combo :clearable="false" :hide-details="true" :byId="true" v-model="reminder.day_of_month" :items="daysOfMonth" class="ml-3 intervention-creation-reminder-selection" style="width: 100px;" data-test="intervention-edit-schedule-reminder-day-of-month" />
                        </div>
                    </template>
                </v-radio>
                <v-radio class="intervention-creation-reminder-radio" value="WEEK" style="height:35px;" data-test="intervention-edit-schedule-reminder-frequency-weekly-radio">
                    <template v-slot:label>
                        <span style="font-size: 14px;">Weekly</span> 
                        <div v-if="reminder.frequency === 'WEEK'" class="d-flex">
                            <div class="ml-1 align-self-center" style="font-size: 14px;">every</div>
                            <fe-remote-combo :clearable="false" :hide-details="true" :byId="true" v-model="reminder.weekly_interval" :items="weeklyIntervals" class="ml-3 intervention-creation-reminder-selection" style="width: 120px;" data-test="intervention-edit-schedule-reminder-weekly-interval" />
                            <div class="align-self-center" style="font-size: 14px;">on</div>
                            <fe-remote-combo :clearable="false" :hide-details="true" :byId="true" v-model="reminder.days_of_week" :items="daysOfWeek" class="ml-3 intervention-creation-reminder-selection" style="width: 120px;" data-test="intervention-edit-schedule-reminder-days-of-week" />
                        </div>
                    </template>
                </v-radio>
            </v-radio-group>
        </fe-dialog>

        <fe-crud
            ref="crudSched"
            :config="$models.interventionAnalysisSchedule"
            @updated="$refs.grid.$refs.crud.read()"
        />
        <fe-crud ref="intvSchedCrud" :config="intvSchedCrudConfig"/>
    </div>
</template>

<script>
import SchedEdit from '../SchedEdit'
import Banner from '@/components/common/Banner'
import InterventionFullscreenSchedule from '@/components/modules/intervention/creation/fullscreen/Schedule'
import {mapState} from 'vuex'

let me = this
const editButton = {
    template: "<div v-if='params.data.enabled' style='color:#006C90; cursor:pointer;'><v-icon color='#006C90' class='mr-3'>far fa-pencil</v-icon>Edit</div>"
}

export default {
        name: 'GroupSchedule',
        components: {
            SchedEdit,
            Banner,
            InterventionFullscreenSchedule,
            editButton,
        },
        props: {
            interventionPlanId: { type: Number, required: true },
            studentPlans: { type: Array, default: () => { return [] }}
        },
        watch: {
            interventionPlanId: {
                handler() { this.loadData() },
                immediate: true
            },
            editingSchedule: {
                deep: true,
                handler(v) {
                    this.changesToBeApplied = []
                    let keysToWatch = ['start_date', 'end_date', 'frequency_cd', 'week_days', 'start_time', 'end_time']
                    if (v?.specific_dates?.length) {
                        keysToWatch.push('specific_dates')
                    }
                    Object.keys(v).forEach(key => {
                        if(keysToWatch.includes(key) && v[key] !== this.schedule[key]) {
                            let val = v[key]
                            let originalVal = this.schedule[key]
                            let keyText = key === 'frequency_cd' ? 'Frequency' : key === 'week_days' ? 'Weekdays' : key.charAt(0).toUpperCase() + key.replace('_', ' ').slice(1)
                            let addToChanged = true
                            if(['start_date', 'end_date'].includes(key)) {
                                val = this.$dayjs(val).format('M/D/YYYY')
                                originalVal = this.$dayjs(originalVal).format('M/D/YYYY')
                            } else if(key.includes('time')) {
                                val = val ? this.$dayjs('2020-01-01 ' + val).format("h:mm A") : 'Time Removed'
                                originalVal = originalVal ? this.$dayjs('2020-01-01 ' + originalVal).format("h:mm A") : 'Time Removed'
                            } else if(key === 'specific_dates') {
                                addToChanged = false
                                if(!originalVal || !val || !originalVal.every((e, i) => e === val[i])) {
                                    let added = val.filter(date => originalVal ? !originalVal.includes(date) : true)
                                    let removed = originalVal ? originalVal.filter(date => !val.includes(date)) : []
                                    if(added.length) {
                                        val = added.map(date => this.$dayjs(date).format('M/D/YYYY')).join(', ')
                                        keyText = 'Dates added'
                                        originalVal = null
                                        this.changesToBeApplied.push({key: keyText, editedValue: val, originalValue: null})
                                    }
                                    if(removed.length) {
                                        val = removed.map(date => this.$dayjs(date).format('M/D/YYYY')).join(', ')
                                        keyText = 'Dates removed'
                                        originalVal = null
                                        this.changesToBeApplied.push({key: keyText, editedValue: val, originalValue: null})
                                    }
                                }
                            }
                            if(val !== originalVal && addToChanged) {
                                this.changesToBeApplied.push({key: keyText, editedValue: val, originalValue: originalVal})
                            } 
                        }
                    })
                }
            },
            'reminder.frequency'(v) {
                if(v === 'MONTH') {
                    this.reminder.day_of_month = 1
                } else if(v === 'WEEK') {
                    this.reminder.days_of_week = 'MON'
                    this.reminder.weekly_interval = 1
                }
            },
            editEmailFrequency(v) {
                if(v && !this.reminder.frequency) {
                    this.reminder.frequency = 'MONTH'
                } 
                if(!v) {
                    this.$refs?.reminderGrid?.gridOptions?.api?.refreshCells({
                        force: true,
                        columns: ['enabled'], 
                    })
                }
            }
        },
        computed: {
            ...mapState('global', ['sessionUser', 'userPreferences']),
            interventionIds() { return this.studentPlans.map(itm => itm.intervention_id) },
            interventionIdString() { return this.interventionIds.join(',') },
            schedule: {
                get: function () {
                    let sched = (this.plan.schedule?.length) ? this.plan.schedule[0] : {}
                    if (sched.start_time) {
                        sched.default_start_time = sched.start_time
                    } else if (sched.default_start_time) {
                        sched.start_time = sched.default_start_time
                    }
                    if (sched.end_time) {
                        sched.default_end_time = sched.end_time
                    } else if (sched.default_end_time) {
                        sched.end_time = sched.default_end_time
                    }
                    return sched
                },
                set: function (sched) {
                    if (sched.start_time) {
                        sched.default_start_time = sched.start_time
                    } else if (sched.default_start_time) {
                        sched.start_time = sched.default_start_time
                    }
                    if (sched.end_time) {
                        sched.default_end_time = sched.end_time
                    } else if (sched.default_end_time) {
                        sched.end_time = sched.default_end_time
                    }
                    this.plan.schedule[0] = sched
                }
            },
            frequencyDisplay() {
                if (this.schedule?.frequency_cd) {
                    let statement = ''
                    let count = this.schedule.frequency_cnt
                    switch (this.schedule.frequency_cd) {
                        case 'WEEK':
                            let days = []
                            this.schedule.week_days.split('').forEach(d => {
                                switch (d) {
                                    case 'M':
                                        days[0] = 'Mon'
                                        break;
                                    case 'T':
                                        days[1] = 'Tues'
                                        break;
                                    case 'W':
                                        days[2] = 'Wed'
                                        break;
                                    case 'H':
                                        days[3] = 'Thurs'
                                        break;
                                    case 'F':
                                        days[4] = 'Fri'
                                        break;
                                }
                            })
                            days = days.filter(Boolean).join(', ')
                            statement += 'Every ' + count
                            statement += (count === 1) ? ' week' : ' weeks'
                            statement += ' on ' + days
                            break;
                        case 'BLOCK':
                            statement += 'Block Schedule'
                            if (count && this.schedule.secondary_cnt) {
                                statement += ' (' + count + ' on - ' + this.schedule.secondary_cnt + ' off)'
                            }
                            break;
                        case 'NOSCHED':
                            statement += 'As Needed'
                            if (count) {
                                statement += ' (' + count + ' total)'
                            }
                    }

                    return statement
                }
            },
            intvSchedCrudConfig() {
                let cfg = this.$_.cloneDeep(this.$models.interventionSchedule)
                cfg.read.params.id = this.schedule.id
                return cfg
            },
            daysOfWeek() {
                return [
                    {id: 'MON', name: 'Monday'},
                    {id: 'TUE', name: 'Tuesday'},
                    {id: 'WED', name: 'Wednesday'},
                    {id: 'THU', name: 'Thursday'},
                    {id: 'FRI', name: 'Friday'},
                ]
            },
            daysOfMonth() {
                return Array.from({length: 28}, (_, i) => ({id: i + 1, name: `${i + 1}` + this.getOrdinalSuffix(`${i + 1}`)}))
            },
            weeklyIntervals() {
                return Array.from({length: 52}, (_, i) => ({id: i + 1, name: `${i+1} week${i === 0 ? '' : 's'}`}))
            },
            topSecretModeActive(){
                return this.$isTopSecretModeActive(this.sessionUser, this.userPreferences)
            },
        },
        data() {
            let me = this
            return {
                plans: [],
                plan: {},
                editSchedRec: null,
                editIntvSched: false,
                editingSchedule: {},
                intvSchedIsValid: false,
                confirmIntvSchedEdit: false,
                schedColDefs: [
                    {
                        headerName: "Student",
                        field: "student_name",
                        headerClass: 'ag-bold-header',
                        sortable: true,
                        editable: false,
                        width: 300,
                        colId: 'studentName',
                        valueFormatter: v => {
                            return this.topSecretModeActive ? v.data.student_id : v.data.student_name
                        }
                    },
                    {
                        headerName: "Enable Emails",
                        headerClass: 'ag-bold-header center-aligned-header',
                        cellRendererFramework: "FeGridToggle",
                        maxWidth: 150,
                        valueGetter: v => {
                            return v.data.enabled
                        },
                        editable: false,
                        sortable: false,
                        onCellClicked: v => {
                            me.reminder = v.data
                            if(!v.data.enabled) {
                                me.editEmailFrequency = true
                            } else {
                                this.reminderDelete()
                            }
                        },
                        colId: 'enabled',
                    },
                    {
                        headerName: "Email Frequency",
                        field: "emailFrequency",
                        headerClass: 'ag-bold-header',
                        sortable: false,
                        editable: false,
                        minWidth: 150,
                        tooltipField: 'emailFrequency'
                    },
                    {
                        headerName: "",
                        cellRendererFramework: 'editButton',
                        maxWidth: 80,
                        onCellClicked: v => {
                            me.editEmailFrequency = true
                            me.reminder = v.data
                            // days_of_week is ['Wednesday'] and have to change to 'WED'
                            me.reminder.days_of_week = v.data.days_of_week?.length ? this.daysOfWeek.find(x => x.name === v.data.days_of_week[0]).id : null
                        },
                        sortable: false
                    }
                ],
                showBanner: false,
                gridOptions: {
                    context: {componentParent: this}
                },
                reminder: {
                    frequency: 'MONTH',
                    dayOfMonth: 1,
                    weeklyInterval: 1,
                    dayOfWeek: 'MON',
                },
                editEmailFrequency: false,
                remindersData: [],
                changesToBeApplied: [],
            }
        },
        methods: {
            loadData() {
                let planUrl = `interventionPlan.php?action=get&property=list&include_schedule=1&v=2&intervention_plan_id=${this.interventionPlanId}`
                this.$axios.get(planUrl)
                    .then(r => {
                        this.plans = this.$ecResponse(r, 'plans')
                        if (this.plans.length) {
                            this.plan = this.plans[0]
                            const updateReviewScheduleBanner = localStorage.getItem('updateReviewScheduleBanner')
                            if(updateReviewScheduleBanner === null || updateReviewScheduleBanner === 'true') {
                                localStorage.setItem('updateReviewScheduleBanner', true)
                                this.showBanner = true
                            }
                        }
                    })
                    .finally(() => {
                        this.reminderGet()
                    })
            },
            processAnalysisData (records) {
                let processed = records?.map((rec, idx) => Object.assign(rec, {
                    student_name: rec.student_lname + ', ' + rec.student_fname,
                    start_date: rec.start_date,
                    end_date: rec.end_date,
                    emailFrequency: this.gridFrequencyRenderer(rec),
                    enabled: rec.enabled
                }))
                this.remindersData = processed
            },
            gridFrequencyRenderer(rec) {
                if(!rec.enabled) return null
                let str, day
                if(rec.monthly_interval) {
                    str = 'Monthly on the '
                    day = rec.day_of_month ? rec.day_of_month + this.getOrdinalSuffix(rec.day_of_month) : '1st'
                } else if(rec.weekly_interval) {
                    str = rec.weekly_interval > 1 ? `Every ${rec.weekly_interval} weeks on ` : 'Weekly on '
                    day = rec.days_of_week?.join(', ')
                }
                return str + day
            },
            frequencyRenderer(rec) {
                if (rec) {
                    let val = rec.frequency_cnt
                    return (val == 1) ? `Every week` : `Every ${val} weeks`
                } else {
                    return ''
                }
            },
            updateSchedule (obj) {
                this.$refs.crudSched.update(obj.schedule[0], { intervention_id: obj.intervention_id })
            },
            doEditIntvSched () {
                if (!this.schedule.id) return
                this.editingSchedule = this.$_.cloneDeep(this.schedule)
                this.intvSchedIsValid = false
                this.editIntvSched = true
            },
            updateIntvSched (confirm) {
                let me = this
                let payload = this.editingSchedule
                payload.default_start_time = payload.start_time
                payload.default_end_time = payload.end_time
                let duration = this.$getTimeDiff(payload.start_time, payload.end_time)
                let isSpecific = payload.specific_dates && payload.specific_dates.length > 0

                let obj = {
                    "id": this.plan.id,
                    "intervention_plan_intervention_week_days": payload.week_days,
                    "intervention_plan_id": this.plan.id,
                    "intervention_plan_user_id": this.plan.intervention_plan_user_id,
                    "intervention_plan_intervention_schedule_id": payload.id,
                    "intervention_plan_intervention_frequency_cnt": payload.frequency_cnt || 1,
                    "intervention_plan_intervention_frequency_cd": payload.frequency_cd,
                    "intervention_plan_intervention_secondary_cnt": payload.secondary_cnt,
                    "intervention_plan_intervention_start_date": isSpecific ? payload.specific_dates[0] : payload.start_date,
                    "intervention_plan_intervention_end_date": isSpecific ? payload.specific_dates[payload.specific_dates.length-1] : payload.end_date,
                    "intervention_plan_intervention_start_time": payload.default_start_time,
                    "intervention_plan_intervention_end_time": payload.default_end_time,
                    "intervention_plan_session_duration": duration,
                    "intervention_plan_start_date": isSpecific ? payload.specific_dates[0] : payload.start_date,
                    "intervention_plan_end_date": isSpecific ? payload.specific_dates[payload.specific_dates.length-1] : payload.end_date,
                }
                if(isSpecific) {
                    obj.intervention_plan_specific_dates = payload.specific_dates
                }

                this.$axios.post('interventionPlan.php?action=update&allow_new_plan=0&property=plan'+(confirm?'&confirm=1':''), obj)
                    .then(r => {
                        if (r.data && r.data.confirm) {
                            me.$messageBox({
                                title: 'Confirm',
                                persistent: true,
                                maxWidth: 800,
                                message: r.data.msg,
                                actions: [{
                                    text: 'Cancel', primary: false,
                                    onClick: () => {
                                        this.editIntvSched = false
                                        this.confirmIntvSchedEdit = false
                                    }
                                }, {
                                    text: 'Update', primary: true,
                                    onClick: () => {
                                       me.updateIntvSched(true)
                                    }
                                }]
                            })
                        } else {
                            this.$ecResponse(r)
                            this.loadData()
                            this.editIntvSched = false
                            this.confirmIntvSchedEdit = false
                        }
                    })
            },
            doNewPhase() {
                this.editIntvSched = false
                this.confirmIntvSchedEdit = false
                this.$emit('new-phase')
            },
            getOrdinalSuffix(n) {
                if(!n) return ''
                const s = ["th", "st", "nd", "rd"]
                const v = parseInt(n) % 100
                return s[(v - 20) % 10] || s[v] || s[0]
            },
            updateScheduleOnChange(rec) {
                let payload = {frequency: rec.frequency}
                if (rec.frequency === 'MONTH') {
                    payload.day_of_month = rec.day_of_month
                } else if (rec.frequency === 'WEEK') {
                    payload.weekly_interval = rec.weekly_interval
                    payload.day_of_week = rec.days_of_week
                }
                payload.enabled = true
                this.$axios.put(this.$models.getUrl('interventionRemindersPerStudent', 'resource', {id: this.interventionPlanId, interventionId: rec.intervention_id}), payload)
                    .then(response => {
                        if (!this.$ecResponse(response)) {
                            this.$snackbars.$emit('new', {
                                text: response.data.message, usage: 'warning'
                            })
                            return
                        } else {
                            this.$snackbars.$emit('new', {
                                text: response.data.message, usage: 'success'
                            })
                            this.reminderGet()
                        }
                    })
                    .catch(err => {
                        this.$snackbars.$emit('new', {
                            text: 'Failed to add reminder', usage: 'error'
                        })
                    })
            },
            closeBanner() {
                localStorage.setItem('updateReviewScheduleBanner', false)
                this.showBanner = false
            },
            reminderGet() {
                this.$axios.get(this.$models.getUrl('interventionReminders', 'resource', { id: this.interventionPlanId }))
                    .then(response => {
                        this.processAnalysisData(response.data.reminders)
                    })
            },
            reminderDelete() {
                this.$axios.delete(this.$models.getUrl('interventionRemindersPerStudent', 'resource', { id: this.interventionPlanId, interventionId: this.reminder.intervention_id }))
                    .then(() => {
                        this.$snackbars.$emit('new', {
                            text: 'Reminder removed from plan', usage: 'success'
                        })
                        this.reminderGet()
                    })
                    .catch(err => {
                        this.$snackbars.$emit('new', {
                            text: 'Failed to remove reminder', usage: 'error'
                        })
                    })
            }
        }
    }
</script>

<style lang="scss">
.center-aligned-header .ag-header-cell-label {
    justify-content: center;
}
</style>

<style lang="scss" scoped>
.intervention-creation-reminder-radio-group {
    ::v-deep .v-input__slot {
        padding-left: 0 !important;
        margin-left: -5px !important;
    }
}

.intervention-creation-reminder-selection {
    ::v-deep .v-input__slot {
        padding-left: 8px !important;
    }
}
</style>
